import { getDefaultWebsitePageId, pageExists, getPageId } from '../redux/website';
import { loadEventSnapshotAndTransform, filterEventSnapshot, loadRegistrationContent,
  getRedirectPageIdIfOnWrongPath } from '../redux/actions';
import { logoutRegistrant, logoutPlanner } from '../redux/registrantLogin/actions';
import { getPagePath, redirectToPage } from '../redux/pathInfo';
import { showLoadingDialog, hideLoadingDialog, hideLoadingOnError, pageTransitionShowLoading,
  pageTransitionHideLoading } from 'nucleus-guestside-site/src/redux/modules/dialogContainer';
import { SET_CURRENT_EVENT_REGISTRATION_ID } from '../redux/registrationForm/regCart/actionTypes';
import {
  getDeclineErrors, getRegisterErrors, getCheckoutErrors, getUpdateErrors, isExcludedByPrivacySettings,
  getModErrors, getCancelErrors
} from '../redux/registrationForm/errors';
import { startModification, startRegistration, startCancelRegistration,
  startDeclineRegistration, finalizeDeclineRegistration,
  calculatePricing, saveRegistration, removeEventRegistrationFromRegCart,
  startWaitlistRegistration, getPricing }
  from '../redux/registrationForm/regCart';
import { restoreRegistration } from '../redux/registrationForm/regCart';
import { populateRegCartVisibleProducts } from '../redux/visibleProducts';
import { removeTravelBookingFromTravelCart } from '../redux/travelCart';
import { startTravelCancellation } from '../redux/travelCart';
import { getPrimaryRegistrationId, isWaitlistCart } from '../redux/registrationForm/regCart/selectors';
import { prepareForOptOutPageLoad, createOptOutPageHandler } from '../widgets/OptOut';
import { prepareForUnsubscribePageLoad, createUnsubscribePageHandler } from '../widgets/Unsubscribe';
import loadPageResources from 'nucleus-widgets/utils/layout/loadPageResources';
import { openAlreadyRegisteredDialog, openDeclineRegistrationDialog,
  openStartNewRegistrationDialogFromPageLanding, openEventWaitlistDialog, openEventTemporaryClosedErrorDialog,
  openNoAdmissionItemAvailableForRegistrationTypeDialog, openEventStatusDialog, openPrivateEventErrorDialog,
  openTransactionInProcessingErrorDialog
} from '../dialogs';
import { setCurrentPage } from '../redux/pathInfo';
import * as eventStatus from 'event-widgets/clients/EventStatus';
import { mapValues } from 'lodash';
import initializeMultiTabTracking, { handlePageChange, promptToTakeOverRegistration,
  isOtherActiveTabOnRegistrationStartPage, isOtherActiveTabOnDeclineStartPage
} from '../initializeMultiTabTracking';
import { isWidgetPresentOnCurrentPage } from '../redux/website/pageContents';
import Logger from '@cvent/nucleus-logging';
import { getCurrentEventRegistrationIdFromURL, urlStrippedCurrentEventRegistrationId,
  addGroupMemberEventRegIdToUrl } from '../utils/registrationUtils';
import { identifyInvitee, setPendingApprovalStatus } from '../redux/persona';
import { regTypeCapacityFull } from '../dialogs/selectionConflictDialogs';
import { hasRegTypeCapacityWarning } from '../redux/registrationForm/warnings';
import * as registrationTypeUtils from 'event-widgets/utils/registrationType';
import { stripQueryParams } from '../utils/queryUtils';
import qs from 'querystring';

// Selectors
import { getRegCartEventSnapshotVersion, isGroupRegistration, isLoggedIn, isNewRegistration, getEventRegistrationId,
  getRegistrationTypeId } from '../redux/selectors/currentRegistrant';
import { canWaitlist, getSiteEditorRegistrationPath, requireRegApproval, isWaitlistEnabled }
  from '../redux/selectors/event';
import { getAssociatedRegistrationPathId, getRegCart } from '../redux/selectors/shared';
import { getRegistrationPathIdOrDefault, getRegistrationPathIdOrNull
} from '../redux/selectors/currentRegistrationPath';
import { getPageWithRegistrationSummary, getPageWithRegistrationType } from '../redux/website/pageContents';
import { getArchivePageId } from '../redux/website/selectors';
import { isPlannerRegMod } from '../redux/userSession';
import {
  CANCELLATION,
  REGISTRATION,
  DECLINE,
  WAITLIST,
  POST_REGISTRATION,
  PENDING_APPROVAL,
  APPROVAL_DENIED,
  GUEST_REGISTRATION,
  isRegistrationPage,
  isPostRegistrationPage
} from '../redux/website/registrationProcesses';
import { createConfirmationPageHandler } from '../routing/confirmationPage';
import { containsRegistrationSummary } from '../utils/registrationUtils';

const LOG = new Logger('event-guestside-site/src/routeHandlers');

const shouldShowEventClosedDialog = (state, isModification) => {
  if (state.event.isArchived) {
    return false;
  }

  return (state.event.status === eventStatus.COMPLETED && !state.userSession.isPlanner)
  || (state.event.status === eventStatus.CLOSED
    && !canWaitlist(state, getAssociatedRegistrationPathId(state, state.userSession.defaultRegTypeId))
    && !isModification && !state.userSession.isPlanner)
  || state.event.status === eventStatus.CANCELLED;
};
/**
* A function to call at the beginning of every route that executes any event
* status related logic to determine if things like redirects or informational
* dialogs need to happen.
* @returns {boolean} True if a redirect is triggered, false otherwise.
*/
function executeEventStatusRules(store, nextRouterState, routerContext, { isModification = false } = {}) {
  const state = store.getState();

  const { userSession: { isPlanner, isPreview }, event: { isArchived } } = state;
  const { history, match: { params: { pageId }, path } } = nextRouterState;

  if (isArchived && !isPlanner && !isPreview &&
      (!path.endsWith('unsubscribe') && !path.endsWith('opt-out') &&
      (!pageId || (pageId !== 'unsubscribeSubscribe' && pageId !== 'optInOut')))) {
    history.replace(getPagePath(state, getArchivePageId(state.website.pluginData)));
    return true;
  }

  // Only execute this logic on the initial website load.
  if (routerContext.isFirstRender) {
    routerContext.isFirstRender = false; // eslint-disable-line no-param-reassign
    // Check the event status to see if we need to open the dialog.
    if (shouldShowEventClosedDialog(state, isModification)) {
      store.dispatch(openEventStatusDialog(state.event.status, state.text.translate));

      // Check if we also need to redirect.
      if (!pageId || (REGISTRATION.isTypeOfPage(state, pageId) || GUEST_REGISTRATION.isTypeOfPage(state, pageId))) {
        history.replace(getPagePath(state, getDefaultWebsitePageId(state)));
        return true;
      }
    } else {
      initializeMultiTabTracking(store, pageId);
    }
  }
  return false;
}

/**
* A function to call at the end of every entry route handler to update the current page
*/
const pageUpdateHandler = (store, nextRouterState) => {
  if (nextRouterState.match.params.pageId) {
    store.dispatch(setCurrentPage(nextRouterState.match.params.pageId));
  }
  if (window) {
    window.scrollTo(0, 0);
  }
};

/**
 * Creates a new route handler which manages the common functionality of all route handlers.
 * This includes permissions to enter the route and loading displays if any aynchronous
 * operations within the route handler are taking too long.
 */
const manageRouteHandler = (route, store, routerContext, eventStatusRuleOptions = undefined) => {
  return async (nextRouterState, setRenderCallback) => {
    const { pageId } = nextRouterState.match.params;
    // Just return early if event status rules trigger a redirect.
    if (executeEventStatusRules(store, nextRouterState, routerContext, eventStatusRuleOptions)) {
      pageUpdateHandler(store, nextRouterState);
      if (setRenderCallback && pageId) {
        setRenderCallback(true, pageId);
      }
      return;
    }
    store.dispatch(showLoadingDialog());
    try {
      await route(nextRouterState);
    } catch (error) {
      store.dispatch(hideLoadingOnError());
      throw error;
    }
    pageUpdateHandler(store, nextRouterState);
    store.dispatch(pageTransitionHideLoading());
    store.dispatch(hideLoadingDialog());
    if (setRenderCallback && pageId) {
      setRenderCallback(true, pageId);
    }
  };
};

const createIndexHandler = (store) => async (nextRouterState) => {
  const state = store.getState();
  nextRouterState.history.replace(getPagePath(state, getDefaultWebsitePageId(state)));
};

function setCurrentRegistrant(currentEventRegistrationId) {
  return {
    type: SET_CURRENT_EVENT_REGISTRATION_ID,
    payload: {
      currentEventRegistrationId
    }
  };
}

const createPageHandler = (store) => async (nextRouterState) => {
  let state = store.getState();
  const { userSession: { regCartId, isTestMode, isPlanner, isPreview } } = state;
  const { history, match: { params: { pageId } } } = nextRouterState;

  // run code snippets for all pages
  if (isTestMode || (!isPlanner && !isPreview)) {
    if (window.CVENT && window.CVENT.runTriggerHandlers) {
      window.CVENT.runTriggerHandlers('AllPages');
    }
  }

  // if page code is specified, redirect to that page. go to summary page if no page with that code is found
  const query = qs.parse(getPagePath(state, pageId).split('?')[1]) || {};
  if (query && query.pc) {
    const newPageId = getPageId(state, query.pc);
    if (newPageId) {
      history.replace(stripQueryParams(getPagePath(state, newPageId), ['pc']));
    } else {
      history.replace(stripQueryParams(getPagePath(state, getDefaultWebsitePageId(state)), ['pc']));
    }
    return;
  }

  if (pageId === 'alreadyRegistered') {
    history.replace(getPagePath(state, getDefaultWebsitePageId(state)));
    store.dispatch(openAlreadyRegisteredDialog({
      title: 'EventGuestSide_ExistingInvitee_AlreadyRegistered_Header__resx'
    }));
    return;
  }

  // Navigate to default page if the page does not exist.
  if (pageId !== 'optInOut' && pageId !== 'unsubscribeSubscribe' && !pageExists(store.getState(), pageId)) {
    history.replace(getPagePath(state, getDefaultWebsitePageId(state)));
    return;
  }

  const currentMemberEventRegistration = nextRouterState.match.params.pageId && getCurrentEventRegistrationIdFromURL(
    getPagePath(state, nextRouterState.match.params.pageId));
  let isGroupReg = isGroupRegistration(state);
  // Handle page refresh and deep linking
  const cartExists = (getRegCart(state) || {}).regCartId;
  const postRegCartExists = (getRegCart(state) || {}).status === 'TRANSIENT';
  if (state.userSession.regCartId && !(cartExists || postRegCartExists)) {
    await store.dispatch(restoreRegistration(regCartId));
    await store.dispatch(calculatePricing());
    await store.dispatch(populateRegCartVisibleProducts());
    state = store.getState();
    // restore currentEventRegistrationId from URL if needed
    isGroupReg = isGroupRegistration(state);
    if (pageId && (REGISTRATION.isTypeOfPage(state, pageId) || GUEST_REGISTRATION.isTypeOfPage(state, pageId))
    && currentMemberEventRegistration.currentEventRegistrationId && isGroupReg) {
      // For pages before reg summary, it is set from URL
      store.dispatch(setCurrentRegistrant(currentMemberEventRegistration.currentEventRegistrationId));
    }
  }

  // reload reg content in order to display reg summary info on confirmation page after refresh
  if (isPostRegistrationPage(state, pageId) && containsRegistrationSummary(store.getState().website, pageId)) {
    await store.dispatch(loadRegistrationContent(REGISTRATION, getRegistrationPathIdOrNull(store.getState())));
    const regCart = getRegCart(state);
    if (regCart && regCart.regCartId) {
      await store.dispatch(getPricing());
    }
  }

  state = store.getState();
  const regCart = getRegCart(state) || {};
  if (isLoggedIn(state)) {
    if (isNewRegistration(state) &&
        regCart.status !== 'INPROGRESS') {
      if (requireRegApproval(state)) {
        await store.dispatch(setPendingApprovalStatus(state));
      }
    } else {
      await store.dispatch(identifyInvitee(state));
    }
  }

  if (pageId && isRegistrationPage(state, pageId) && ((regCart.status === 'COMPLETED'
    || regCart.status === 'PROCESSING' || regCart.status === 'QUEUED' ||
    regCart.status === 'PAYMENT_INITIATED' || regCart.status === 'THIRD_PARTY_REDIRECT' ||
    regCart.status === 'THIRD_PARTY_REDIRECT_STARTED') || (
    regCart.status === 'INPROGRESS' && regCart.paymentInfo
    && regCart.paymentInfo.paymentStatus === 'PaymentFailed'))) {
    const navWidgetModule = import('../widgets/RegistrationNavigatorWidget');
    // Completed - Should take you to the confirmation page
    // Processing / Queued / Payment Initiated / Third Party Redirect - Continue to wait and poll reg cart status
    if (regCart.status !== 'COMPLETED') {
      await store.dispatch(loadPageResources(store.getState().website, pageId));
    }
    await store.dispatch(getPricing());
    const { continueRegistration } = await navWidgetModule;
    await store.dispatch(continueRegistration(state));
    return;
  }

  // If the page being accessed is on the wrong reg path, redirect to the right one
  const redirectPageId = await store.dispatch(getRedirectPageIdIfOnWrongPath(pageId));
  if (redirectPageId) {
    LOG.info(`redirecting to ${redirectPageId} because on wrong path`);
    history.replace(getPagePath(state, redirectPageId));
    return;
  }

  const URL = getPagePath(state, pageId);
  const currentEventRegistrationIdFromState = getEventRegistrationId(state);
  const primaryEventRegistrationId = getPrimaryRegistrationId(regCart);
  const currentEventRegistrationIdFromURL = getCurrentEventRegistrationIdFromURL(URL);
  // This is for switching back to group leaders eventRegistrationId when on a reg page with regSummary widget
  if (pageId && isRegistrationPage(state, pageId) &&
    isWidgetPresentOnCurrentPage(state.website, 'RegistrationSummary', pageId) && isGroupReg) {
    // remove the eventRegistration if we got here without completing the group members registration.
    if (!currentMemberEventRegistration.memberCompletedReg &&
      currentEventRegistrationIdFromState !== primaryEventRegistrationId) {
      LOG.debug('Removing travel bookings for the eventRegistration with id ', currentEventRegistrationIdFromState);
      await store.dispatch(removeTravelBookingFromTravelCart(currentEventRegistrationIdFromState));
      await store.dispatch(removeEventRegistrationFromRegCart(currentEventRegistrationIdFromState));
      // saveRegistration so that the removed event reg is persisted in reg API
      await store.dispatch(saveRegistration());
    }
    // return to the group leader's registration and update state so we can do further routing
    store.dispatch(setCurrentRegistrant(primaryEventRegistrationId));
    state = store.getState();
    // the leader could be on a different path with a different summary page, so redirect to the summary page
    let primaryRegSummaryPage = getPageWithRegistrationSummary(state);
    if (!primaryRegSummaryPage) {
      await store.dispatch(loadRegistrationContent(REGISTRATION, getRegistrationPathIdOrDefault(state)));
      state = store.getState();
      primaryRegSummaryPage = getPageWithRegistrationSummary(state);
    }
    const primaryRegSummaryPathId = primaryRegSummaryPage && primaryRegSummaryPage.id;
    if (primaryRegSummaryPathId !== pageId) {
      history.replace(urlStrippedCurrentEventRegistrationId(getPagePath(state, primaryRegSummaryPathId)));
      return;
    }
    history.replace(urlStrippedCurrentEventRegistrationId(getPagePath(state, pageId)));
  } else if (pageId && isRegistrationPage(state, pageId)) {
    // Remove currentEventRegistrationId from URL if the id in the state is the group leaders or if there is a mismatch
    if (currentEventRegistrationIdFromURL && currentEventRegistrationIdFromState === primaryEventRegistrationId) {
      history.replace(urlStrippedCurrentEventRegistrationId(URL));
    } else if (currentEventRegistrationIdFromState && currentEventRegistrationIdFromURL &&
      currentEventRegistrationIdFromURL !== currentEventRegistrationIdFromState) {
      history.replace(addGroupMemberEventRegIdToUrl(urlStrippedCurrentEventRegistrationId(URL),
        currentEventRegistrationIdFromState));
    }
  } else if (pageId && !isRegistrationPage(state, pageId)) {
    // Remove currentEventRegistrationId from URL
    store.dispatch(setCurrentRegistrant(primaryEventRegistrationId));
    history.replace(urlStrippedCurrentEventRegistrationId(getPagePath(state, pageId)));
  }
  // Individual pages access rules
  // 1. Registration pages require active Registration regCart
  if (isRegistrationPage(state, pageId) && (!regCart.regCartId || regCart.regDecline)) {
    if (isPlannerRegMod(state)) {
      history.replace(getPagePath(state, 'modifyRegistration'));
    } else {
      history.replace(getPagePath(state, 'register'));
    }
    return;
  }
  // 2. Post Reg, registration pending approval, registration denied, and Cancellation pages
  // require user logged in (confirmed registration)
  if ((POST_REGISTRATION.isTypeOfPage(state, pageId) || CANCELLATION.isTypeOfPage(state, pageId) ||
       PENDING_APPROVAL.isTypeOfPage(state, pageId) || APPROVAL_DENIED.isTypeOfPage(state, pageId)) &&
      !isLoggedIn(state)) {
    history.replace(getPagePath(state, getDefaultWebsitePageId(state)));
    return;
  }
  // 3. Decline Survey page require active Decline regCart
  if (DECLINE.isTypeOfPage(state, pageId) && !(regCart.regCartId && regCart.regDecline)) {
    await store.dispatch(logoutRegistrant());
    store.dispatch(redirectToPage('decline'));
    return;
  }

  if (pageId === 'optInOut') { // dynamically inserted guestside-only pages
    await store.dispatch(prepareForOptOutPageLoad());
  } else if (pageId === 'unsubscribeSubscribe') {
    await store.dispatch(prepareForUnsubscribePageLoad());
  }
  await store.dispatch(loadPageResources(store.getState().website, pageId));
  store.dispatch(handlePageChange(pageId));
};

const createStartRegistrationHandler = (store) => async (nextRouterState) => {
  const initialState = store.getState();
  const { regCartId: regCartIdFromSession, eventId, inviteeId,
    contactId, defaultRegTypeId } = initialState.userSession;
  const regCartIdFromState = (getRegCart(initialState) || {}).regCartId;
  const { history } = nextRouterState;
  if (regCartIdFromSession || regCartIdFromState) {
    nextRouterState.history.replace(getPagePath(initialState, getDefaultWebsitePageId(initialState)));
    await store.dispatch(identifyInvitee());
    await store.dispatch(restoreRegistration(regCartIdFromSession || regCartIdFromState));
    const updatedState = store.getState();
    if (isWaitlistCart(updatedState.registrationForm.regCart)) {
      await store.dispatch(logoutRegistrant());
      store.dispatch(openEventWaitlistDialog());
    } else {
      store.dispatch(openStartNewRegistrationDialogFromPageLanding({
        title: '_registerNowWidget_textConfig__resx',
        style: initialState.website.theme.global
      }));
    }
    return;
  }
  try {
    await store.dispatch(startRegistration(eventId, inviteeId, contactId, defaultRegTypeId));
  } catch (error) {
    if (isExcludedByPrivacySettings(error, store.getState())
    || getUpdateErrors.isAttendeeNotAllowedByCustomLogic(error, store.getState())) {
      await store.dispatch(openPrivateEventErrorDialog());
      history.replace(getPagePath(store.getState(), ''));
      return;
    }
    if (getRegisterErrors.isInviteeRegistered(error)) {
      history.replace(getPagePath(store.getState(), getDefaultWebsitePageId(store.getState())));
      await store.dispatch(openAlreadyRegisteredDialog({ title: 'EventGuestSide_ExistingInvitee_AlreadyRegistered_Header__resx' }));
      return;
    }
    if (getRegisterErrors.isInviteeAlreadyWaitlistedForClosedEvent(error)) {
      history.replace(getPagePath(store.getState(), getDefaultWebsitePageId(store.getState())));
      await store.dispatch(openEventWaitlistDialog());
      return;
    }

    const waitlistEnabled = isWaitlistEnabled(store.getState(),
      getAssociatedRegistrationPathId(store.getState(), store.getState().userSession.defaultRegTypeId));
    if (getRegisterErrors.isEventTemporaryClosed(error)) {
      await store.dispatch(openEventTemporaryClosedErrorDialog());
      return;
    }
    if ((getRegisterErrors.isEventClosed(error) || getRegisterErrors.isCapacityFull(error)) &&
      waitlistEnabled) {
      try {
        await store.dispatch(startWaitlistRegistration(inviteeId));
      } catch (waitlistError) {
        if (getRegisterErrors.isInviteeRegistered(waitlistError)) {
          history.replace(getPagePath(store.getState(), getDefaultWebsitePageId(store.getState())));
          await store.dispatch(openAlreadyRegisteredDialog({}));
          return;
        } else if (getRegisterErrors.isInviteeAlreadyWaitlistedForClosedEvent(waitlistError)) {
          history.replace(getPagePath(store.getState(), getDefaultWebsitePageId(store.getState())));
          await store.dispatch(openEventWaitlistDialog());
          return;
        } else if (getRegisterErrors.isPrivateEvent(waitlistError)
          || getRegisterErrors.isAttendeeNotAllowedByCustomLogic(waitlistError)) {
          await store.dispatch(openPrivateEventErrorDialog());
          history.replace(getPagePath(store.getState(), ''));
          return;
        } else if (getRegisterErrors.isEventTemporaryClosed(waitlistError)) {
          await store.dispatch(openEventTemporaryClosedErrorDialog());
          return;
        }
        throw waitlistError;
      }
      await store.dispatch(loadRegistrationContent(WAITLIST, getRegistrationPathIdOrNull(store.getState())));
      history.replace(WAITLIST.forCurrentRegistrant().startPagePath(store.getState()));
      return;
    }
    if (getRegisterErrors.isRegistrationClosed(error) || getRegisterErrors.isEventClosed(error)) {
      history.replace(getPagePath(store.getState(), getDefaultWebsitePageId(store.getState())));
      store.dispatch(openEventStatusDialog(eventStatus.CLOSED, store.getState().text.translate));
      return;
    }
    if (getCheckoutErrors.admissionItemMissing(error)) {
      const { userSession: { isPlanner }, plannerRegSettings: { exitUrl } } = store.getState();
      store.dispatch(openNoAdmissionItemAvailableForRegistrationTypeDialog(() => {
        return (dispatch, getState) => {
          if (isPlanner) {
            dispatch(logoutPlanner(exitUrl));
          }
          dispatch(redirectToPage(getDefaultWebsitePageId(getState())));
        };
      }));
      return;
    }
    throw error;
  }
  const regTypeId = getRegistrationTypeId(store.getState());
  await store.dispatch(loadRegistrationContent(REGISTRATION, getRegistrationPathIdOrNull(store.getState())));
  if (regTypeId !== registrationTypeUtils.defaultRegistrationTypeId && hasRegTypeCapacityWarning(store.getState())) {
    const capacityFull = await regTypeCapacityFull(store.getState(),
      getAssociatedRegistrationPathId(store.getState(), regTypeId));
    const regTypeWidgetDoesNotExist = !getPageWithRegistrationType(store.getState());
    if (capacityFull || regTypeWidgetDoesNotExist) {
      history.replace(getPagePath(store.getState(), getDefaultWebsitePageId(store.getState())));
      store.dispatch(openEventStatusDialog(eventStatus.CLOSED, store.getState().text.translate));
      return;
    }
  }
  history.replace(REGISTRATION.forCurrentRegistrant().startPagePath(store.getState()));
};

const createModifyRegistrationHandler = (store) => async (nextRouterState) => {
  if (isOtherActiveTabOnRegistrationStartPage(store.getState())) {
    store.dispatch(promptToTakeOverRegistration());
    return;
  }
  try {
    await store.dispatch(startModification());
  } catch (error) {
    if (getModErrors.isTransactionInProcessingError(error)) {
      await store.dispatch(openTransactionInProcessingErrorDialog());
      return;
    }
    throw error;
  }
  await Promise.all([
    store.dispatch(loadEventSnapshotAndTransform(getRegCartEventSnapshotVersion(store.getState()))),
    store.dispatch(loadRegistrationContent(REGISTRATION, getRegistrationPathIdOrNull(store.getState())))
  ]);
  nextRouterState.history.replace(REGISTRATION.forCurrentRegistrant().startPagePath(store.getState()));
};

const createDeclineRegistrationHandler = (store) => async (nextRouterState) => {
  const { history } = nextRouterState;
  let state = store.getState();
  const defaultWebsitePageId = getDefaultWebsitePageId(state);
  if (!(state.event.status === eventStatus.ACTIVE || state.event.status === eventStatus.CLOSED
    || (state.event.status === eventStatus.COMPLETED && state.userSession.isPlanner)
    || (state.event.status === eventStatus.PENDING
      && (state.userSession.isPlanner || state.userSession.isTestMode)))) {
    history.replace(getPagePath(state, defaultWebsitePageId));
    return;
  }
  const { userSession: { regCartId: regCartIdFromSession, inviteeId } } = state;
  // To prevent prior session causing 401 error. FLEX-12134 improves below:
  if (regCartIdFromSession && !isOtherActiveTabOnDeclineStartPage(state)) {
    await store.dispatch(logoutRegistrant());
  }
  if (inviteeId) {
    let error = null;
    try {
      await store.dispatch(startDeclineRegistration(inviteeId));
    } catch (ex) {
      error = ex;
    }
    if (!error) {
      const regPathId = getRegistrationPathIdOrDefault(store.getState());
      let registrationSettings = getSiteEditorRegistrationPath(store.getState(), regPathId);
      if (!registrationSettings) {
        const regTypeId = getRegistrationTypeId(store.getState());
        await store.dispatch(filterEventSnapshot(store.getState().eventSnapshotVersion, regTypeId, regPathId));
        registrationSettings = getSiteEditorRegistrationPath(store.getState(), regPathId);
      }
      if (registrationSettings.decline.hasDeclinePage) {
        await Promise.all([
          store.dispatch(loadEventSnapshotAndTransform(getRegCartEventSnapshotVersion(store.getState()))),
          store.dispatch(loadRegistrationContent(DECLINE, getRegistrationPathIdOrNull(store.getState())))
        ]);
        nextRouterState.history.replace(DECLINE.forCurrentRegistrant().startPagePath(store.getState()));
        return;
      }
      // event's created before Decline Survey feature exists: no decline page, just call services
      try {
        await store.dispatch(finalizeDeclineRegistration());
      } catch (ex) {
        error = ex;
      }
    }
    if (error) {
      if (getDeclineErrors.isInviteeRegistered(error)) {
        store.dispatch(openAlreadyRegisteredDialog({ title: 'EventGuestSide_ExistingInvitee_AlreadyRegistered_Header__resx' }));
      } else if (getDeclineErrors.isInviteeDeclined(error)) {
        store.dispatch(openDeclineRegistrationDialog());
      } else if (getDeclineErrors.isInviteeStatusInvalid(error)) {
        LOG.warn('invitee status is invalid for requested action: Decline Registration', error);
      } else {
        throw error;
      }
      history.replace(getPagePath(state, defaultWebsitePageId));
      return;
    }
  }
  store.dispatch(logoutRegistrant());
  store.dispatch(openDeclineRegistrationDialog());
  history.replace(getPagePath(state, defaultWebsitePageId));
};

const createCancelRegistrationHandler = (store) => async (nextRouterState) => {
  try {
    await store.dispatch(startCancelRegistration());
  } catch (error) {
    if (getCancelErrors.isTransactionInProcessingError(error)) {
      await store.dispatch(openTransactionInProcessingErrorDialog());
      return;
    }
    throw error;
  }
  await Promise.all([
    store.dispatch(loadRegistrationContent(CANCELLATION, getRegistrationPathIdOrNull(store.getState()))),
    store.dispatch(loadEventSnapshotAndTransform(getRegCartEventSnapshotVersion(store.getState())))
  ]);
  await store.dispatch(startTravelCancellation(getRegCart(store.getState()).regCartId));
  await store.dispatch(calculatePricing());
  nextRouterState.history.replace(CANCELLATION.forCurrentRegistrant().startPagePath(store.getState()));
};
const createWaitlistHandler = (store) => async (nextRouterState) => {
  const state = store.getState();
  const { userSession: { inviteeId } } = state;
  if (state.userSession.isPlanner
    || isWaitlistEnabled(state,
      getAssociatedRegistrationPathId(state, state.userSession.defaultRegTypeId))) {
    await store.dispatch(startWaitlistRegistration(inviteeId));
    await store.dispatch(loadRegistrationContent(WAITLIST, getRegistrationPathIdOrNull(store.getState())));
    nextRouterState.history.replace(WAITLIST.forCurrentRegistrant().startPagePath(store.getState()));
    return;
  }
  nextRouterState.history.replace(getPagePath(state, getDefaultWebsitePageId(state)));
};

const createPageLeaveHandler = (store) => () => {
  store.dispatch(pageTransitionShowLoading());
};

export const createRouteHandlers = (store) => {
  const { userSession: { isPlanner, isPreview }, event: { isArchived } } = store.getState();
  let routerContext = { isFirstRender: true };
  const routes = {
    index: createIndexHandler(store),
    page: createPageHandler(store),
    startRegistration: (isArchived && !isPlanner && !isPreview) ? () => {} :
      createStartRegistrationHandler(store),
    modifyRegistration: createModifyRegistrationHandler(store),
    declineRegistration: createDeclineRegistrationHandler(store),
    cancelRegistration: createCancelRegistrationHandler(store),
    waitlist: createWaitlistHandler(store),
    confirmationPage: createConfirmationPageHandler(store),
    optOut: createOptOutPageHandler(store),
    unsubscribe: createUnsubscribePageHandler(store)
  };
  return {
    ...mapValues(routes, route => manageRouteHandler(route, store, routerContext)),
    modifyRegistration: manageRouteHandler(routes.modifyRegistration, store, routerContext, { isModification: true }),
    // skip first render handling for transient action URLS (real pages they redirect to should perform the check)
    declineRegistration: manageRouteHandler(routes.declineRegistration, store, { isFirstRender: false }),
    cancelRegistration: manageRouteHandler(routes.cancelRegistration, store, { isFirstRender: false }),
    optOut: manageRouteHandler(routes.optOut, store, { isFirstRender: false }),
    unsubscribe: manageRouteHandler(routes.unsubscribe, store, { isFirstRender: false }),
    pageLeave: createPageLeaveHandler(store)
  };
};
